import { useCallback, useMemo } from "react";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Column, Row } from "@covertree/ui/table/types";

import {
  createColumnValueChecker,
  mapPolicyType,
  mapResidentInsuranceAdoptionStatus,
  RemovableResidentColumn,
} from "../utils";

import CognitoUserGroup from "@enums/CognitoUserGroup";

import { useAuthContext } from "@covertree/auth";
import { formatTimestamp } from "@covertree/utils";
import { ResidentListItem } from "@graphql/custom/api";
import { InsuranceAdoptionStatus } from "@graphql/api";
import { getAddressLine1WithoutUnit } from "@utils";
import { RESIDENTS_ROUTES } from "@routing/Residents";

export const useMappedResidents = (
  residents: ResidentListItem[] | undefined,
  columns: Column[]
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { hasGroup } = useAuthContext();

  const openResidentDetails = useCallback(
    (id: string) => {
      navigate({
        pathname: `${RESIDENTS_ROUTES.RESIDENT}/${encodeURIComponent(id)}`,
        search: createSearchParams(searchParams).toString(),
      });
    },
    [searchParams]
  );

  const openReviewDetails = useCallback(
    (id: string) => {
      navigate({
        pathname: `${RESIDENTS_ROUTES.REVIEW}/${encodeURIComponent(id)}`,
        search: createSearchParams(searchParams).toString(),
      });
    },
    [searchParams]
  );

  return useMemo(() => {
    if (!residents) {
      return [];
    }

    const isColumnDefinedForValue = createColumnValueChecker(columns);
    return residents.map<Row>(({ siteAddress, ...resident }) => {
      return {
        id: resident.id,
        data: [
          resident.residentId,
          `${resident.firstName} ${resident.lastName}`,
          [
            getAddressLine1WithoutUnit(siteAddress.addressLine1),
            siteAddress.city,
            siteAddress.state,
            siteAddress.zipCode,
          ].join(" "),
          siteAddress.lotUnit,
          ...isColumnDefinedForValue(
            RemovableResidentColumn.InsuranceAdoptionStatus,
            mapResidentInsuranceAdoptionStatus(
              resident.insuranceAdoption.status
            )
          ),
          ...isColumnDefinedForValue(
            RemovableResidentColumn.PolicyType,
            mapPolicyType(
              resident.policyType,
              resident.insuranceAdoption.status
            )
          ),
          resident.community.name,
          ...isColumnDefinedForValue(
            RemovableResidentColumn.CommunityPortfolio,
            resident.community.portfolio.name
          ),
          formatTimestamp(resident.createdAt),
          formatTimestamp(resident.updatedAt),
        ],
        onClickRow: () => openResidentDetails(resident.id),
        actionMenuItems: [
          {
            label: "View Resident Details",
            onClick: () => openResidentDetails(resident.id),
          },
          ...(resident.insuranceAdoption.status ===
            InsuranceAdoptionStatus.UnderReview &&
          hasGroup([
            CognitoUserGroup.CoverTreeExecutive,
            CognitoUserGroup.CoverTreeEmployee,
            CognitoUserGroup.CommunityPortfolioExecutive,
            CognitoUserGroup.CommunityManager,
          ])
            ? [
                {
                  label: "View Review Details",
                  onClick: () => openReviewDetails(resident.id),
                },
              ]
            : []),
        ],
      };
    });
  }, [residents, columns, hasGroup, openResidentDetails, openReviewDetails]);
};
